import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Mail, Briefcase, Trash2, Eye, EyeOff, CheckCircle2, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ContactsTab({ contacts, onRefresh }) {
  const handleMarkRead = async (id, isRead) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}/${isRead ? "unread" : "read"}`, {}, { headers: getAuthHeaders() });
      onRefresh();
    } catch (err) {
      console.error("Error toggling read:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: getAuthHeaders() });
      onRefresh();
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  if (contacts.length === 0) {
    return (
      <div className="text-center py-16 text-slate-500">
        <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p className="font-['Plus_Jakarta_Sans']">No contact submissions yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {contacts.map((c) => (
        <div
          key={c.id}
          data-testid={`contact-item-${c.id}`}
          className={`p-6 border transition-colors ${c.is_read ? "bg-white border-slate-200" : "bg-blue-50 border-blue-200"}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-slate-900 font-['Outfit']">{c.name}</h3>
                {!c.is_read && (
                  <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-medium">NEW</span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mb-3">
                <span>{c.email}</span>
                {c.phone && <span>{c.phone}</span>}
                {c.company && <span>{c.company}</span>}
                {c.service_interest && (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs">{c.service_interest}</span>
                )}
              </div>
              <p className="text-slate-600 text-sm font-['Plus_Jakarta_Sans'] leading-relaxed">{c.message}</p>
              <p className="text-xs text-slate-400 mt-3">
                <Clock className="w-3 h-3 inline mr-1" />
                {formatDate(c.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleMarkRead(c.id, c.is_read)}
                className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                title={c.is_read ? "Mark as unread" : "Mark as read"}
                data-testid={`contact-toggle-read-${c.id}`}
              >
                {c.is_read ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => handleDelete(c.id)}
                className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                title="Delete"
                data-testid={`contact-delete-${c.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApplicationsTab({ applications, onRefresh }) {
  const handleMarkReviewed = async (id) => {
    try {
      await axios.patch(`${API}/admin/applications/${id}/reviewed`, {}, { headers: getAuthHeaders() });
      onRefresh();
    } catch (err) {
      console.error("Error marking reviewed:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, { headers: getAuthHeaders() });
      onRefresh();
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  if (applications.length === 0) {
    return (
      <div className="text-center py-16 text-slate-500">
        <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p className="font-['Plus_Jakarta_Sans']">No job applications yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {applications.map((a) => (
        <div
          key={a.id}
          data-testid={`application-item-${a.id}`}
          className={`p-6 border transition-colors ${a.is_reviewed ? "bg-white border-slate-200" : "bg-green-50 border-green-200"}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-slate-900 font-['Outfit']">{a.name}</h3>
                {a.is_reviewed ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Reviewed
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium">Pending</span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mb-2">
                <span>{a.email}</span>
                {a.phone && <span>{a.phone}</span>}
              </div>
              <p className="text-sm font-medium text-blue-700 mb-2">
                Applied for: {a.job_title}
              </p>
              {a.linkedin_url && (
                <a href={a.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                  LinkedIn Profile
                </a>
              )}
              {a.cover_letter && (
                <p className="text-slate-600 text-sm font-['Plus_Jakarta_Sans'] leading-relaxed mt-2">{a.cover_letter}</p>
              )}
              <p className="text-xs text-slate-400 mt-3">
                <Clock className="w-3 h-3 inline mr-1" />
                {formatDate(a.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!a.is_reviewed && (
                <button
                  onClick={() => handleMarkReviewed(a.id)}
                  className="p-2 text-slate-400 hover:text-green-600 transition-colors"
                  title="Mark as reviewed"
                  data-testid={`application-review-${a.id}`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => handleDelete(a.id)}
                className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                title="Delete"
                data-testid={`application-delete-${a.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [contactsRes, appsRes, meRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/applications`, { headers }),
        axios.get(`${API}/admin/me`, { headers }),
      ]);
      setContacts(contactsRes.data);
      setApplications(appsRes.data);
      setAdminName(meRes.data.name);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    axios.post(`${API}/admin/logout`, {}, { headers: getAuthHeaders() }).catch(() => {});
    navigate("/admin");
  };

  const unreadContacts = contacts.filter(c => !c.is_read).length;
  const pendingApps = applications.filter(a => !a.is_reviewed).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-100">
      {/* Top Bar */}
      <header className="bg-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-bold text-white font-['Outfit']">Nidus Admin</h1>
            <span className="text-slate-500 text-sm">|</span>
            <span className="text-slate-400 text-sm">{adminName}</span>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 rounded-none text-sm"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Total Contacts</p>
            <p className="text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="stat-total-contacts">{contacts.length}</p>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Unread</p>
            <p className="text-3xl font-bold text-blue-600 font-['Outfit']" data-testid="stat-unread-contacts">{unreadContacts}</p>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Total Applications</p>
            <p className="text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="stat-total-apps">{applications.length}</p>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Pending Review</p>
            <p className="text-3xl font-bold text-orange-600 font-['Outfit']" data-testid="stat-pending-apps">{pendingApps}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6">
          <button
            onClick={() => setActiveTab("contacts")}
            data-testid="tab-contacts"
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "contacts"
                ? "bg-white text-slate-900 border border-slate-200 border-b-white -mb-px relative z-10"
                : "bg-slate-200 text-slate-600 hover:bg-slate-300"
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Contact Submissions
            {unreadContacts > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">{unreadContacts}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("applications")}
            data-testid="tab-applications"
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "applications"
                ? "bg-white text-slate-900 border border-slate-200 border-b-white -mb-px relative z-10"
                : "bg-slate-200 text-slate-600 hover:bg-slate-300"
            }`}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            Job Applications
            {pendingApps > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">{pendingApps}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-slate-200 p-6">
          {activeTab === "contacts" ? (
            <ContactsTab contacts={contacts} onRefresh={fetchData} />
          ) : (
            <ApplicationsTab applications={applications} onRefresh={fetchData} />
          )}
        </div>
      </div>
    </div>
  );
}
